import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { currentPeriod, fmtDate, fmtTime, inr, minutesToHm, P, type Employee, type ID, type LifecycleEvent } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { humanize, todayISO } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { openFile } from '../../../lib/files';
import { DOCUMENT_HINT, pickDocument } from '../../../lib/picker';
import { useMut } from '../../../lib/query';
import { appendFile, type UploadFile } from '../../../lib/upload';
import { useGo, useParams } from '../../../navigation/nav';
import { BalanceCards } from '../../ess/components/BalanceCards';
import { AttendanceLegend, attBg, attColor } from '../../ess/components/attendance';
import { copyText } from '../components/clipboard';
import { EmployeePicker } from '../components/EmployeePicker';
import { fmtVal } from '../components/misc';
import { useOrgOptions } from '../components/orgOptions';
import {
  Alert, Avatar, Badge, Button, CalChip, CalendarMonth, Card, Columns, ConfirmDialog, DataTable, DateField, DescList, Dot, EmptyState, ErrorState, Grid, Icon, IconButton,
  ListItem, Loading, Modal, PageHeader, Row, Screen, Select, Stack, StatusBadge, Tabs, Text, TextField, radius, useTheme, useToast, type IconName,
} from '../../../ui';

type Sensitive = Pick<Employee, 'pan' | 'aadhaar' | 'uan' | 'esic_number' | 'bank_account_number' | 'bank_ifsc'>;
type TabKey = 'overview' | 'jobs' | 'documents' | 'lifecycle' | 'salary' | 'attendance' | 'leave';
type LcType = LifecycleEvent['type'];
const LC_ICON: Record<LcType, IconName> = { join: 'plus', confirm: 'check', transfer: 'mapPin', promote: 'chevronUp', exit: 'logout' };

function Overview({ emp }: { emp: Employee }) {
  const { can } = useAuth();
  const go = useGo();
  const [revealed, setRevealed] = useState<Sensitive | null>(null);
  const reveal = useMut(() => api.employees.sensitive(emp.id), { onSuccess: (r) => setRevealed(r), success: 'Access logged in the audit trail' });
  const s = revealed ?? emp;
  const j = emp.current_job;
  const custom = Object.entries(emp.custom_fields ?? {});
  return (
    <Stack>
      <Columns ratio={[1, 1]}>
        <Card title="Personal">
          <DescList
            items={[
              ['Work email', emp.email], ['Personal email', emp.personal_email], ['Mobile', emp.phone], ['Date of birth', fmtDate(emp.date_of_birth)],
              ['Gender', humanize(emp.gender)], ['Marital status', humanize(emp.marital_status)], ['Blood group', emp.blood_group], ['Address', emp.address],
            ]}
          />
        </Card>
        <Card title="Job">
          <DescList
            items={[
              ['Designation', j?.designation?.name ?? emp.designation], ['Department', j?.department?.name ?? emp.department], ['Location', j?.location?.name ?? emp.location],
              ['Legal entity', j?.legal_entity?.name], ['Grade', j?.grade?.name], ['Cost centre', j?.cost_centre?.name],
              ['Manager', j?.manager ? <Button size="sm" variant="ghost" title={j.manager.full_name} onPress={() => go('EmployeeDetail', { id: j.manager!.id })} style={{ marginLeft: -10 }} /> : '—'],
              ['Employment type', humanize(j?.employment_type)], ['Joined', fmtDate(emp.date_of_joining)], ['Confirmed', fmtDate(emp.confirmation_date)], ['Exit', fmtDate(emp.exit_date)],
            ]}
          />
        </Card>
      </Columns>
      <Card
        title="Statutory & bank"
        subtitle={revealed ? 'Showing unmasked values' : 'Sensitive values are masked'}
        actions={
          can(P.SENSITIVE_VIEW) ? (
            revealed ? (
              <Button size="sm" icon="lock" title="Hide" onPress={() => setRevealed(null)} />
            ) : (
              <Button size="sm" icon="eye" title="Reveal" loading={reveal.isPending} onPress={() => reveal.mutate(undefined)} testID="emp-reveal" />
            )
          ) : undefined
        }
      >
        <DescList items={[['PAN', s.pan], ['Aadhaar', s.aadhaar], ['UAN', s.uan], ['ESIC', s.esic_number], ['Bank', emp.bank_name], ['Account', s.bank_account_number], ['IFSC', s.bank_ifsc]]} />
      </Card>
      {custom.length > 0 && (
        <Card title="Custom fields">
          <DescList items={custom.map(([k, v]) => [humanize(k), fmtVal(v)])} />
        </Card>
      )}
    </Stack>
  );
}

function JobHistory({ id }: { id: ID }) {
  const q = useQuery({ queryKey: ['employee', id, 'jobs'], queryFn: () => api.employees.jobs(id) });
  return (
    <DataTable
      rows={q.data}
      loading={q.isLoading}
      emptyTitle="No job history"
      columns={[
        { key: 'effective_from', title: 'From', width: 110, render: (r) => fmtDate(r.effective_from), sortValue: (r) => r.effective_from },
        { key: 'effective_to', title: 'To', width: 110, render: (r) => (r.effective_to ? fmtDate(r.effective_to) : <Badge tone="success">Current</Badge>), sortValue: (r) => r.effective_to ?? '9999' },
        { key: 'designation', title: 'Designation', width: 150, render: (r) => r.designation?.name ?? '—', sortValue: (r) => r.designation?.name },
        { key: 'department', title: 'Department', width: 140, render: (r) => r.department?.name ?? '—', sortValue: (r) => r.department?.name },
        { key: 'location', title: 'Location', width: 120, render: (r) => r.location?.name ?? '—', sortValue: (r) => r.location?.name },
        { key: 'grade', title: 'Grade', width: 90, render: (r) => r.grade?.name ?? '—', sortValue: (r) => r.grade?.name },
        { key: 'manager', title: 'Manager', width: 150, render: (r) => r.manager?.full_name ?? '—', sortValue: (r) => r.manager?.full_name },
        { key: 'employment_type', title: 'Type', width: 100, render: (r) => humanize(r.employment_type), sortValue: (r) => r.employment_type },
      ]}
    />
  );
}

const DOC_TYPES = ['offer_letter', 'id_proof', 'address_proof', 'pan', 'education', 'other'];

function Documents({ id }: { id: ID }) {
  const { can } = useAuth();
  const { colors } = useTheme();
  const toast = useToast();
  const manage = can(P.EMPLOYEES_MANAGE);
  const q = useQuery({ queryKey: ['employee', id, 'documents'], queryFn: () => api.employees.documents(id) });
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: 'id_proof', name: '', expiry_date: '' });
  const [file, setFile] = useState<UploadFile | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [del, setDel] = useState<ID | null>(null);
  const inv = { invalidate: [['employee', id, 'documents']] };
  const upload = useMut(
    () => {
      const fd = new FormData();
      fd.append('type', f.type);
      fd.append('name', f.name || file?.name || humanize(f.type));
      if (f.expiry_date) fd.append('expiry_date', f.expiry_date);
      appendFile(fd, 'file', file);
      return api.employees.uploadDocument(id, fd);
    },
    { ...inv, success: 'Document uploaded', onSuccess: () => { setOpen(false); setFile(null); setF({ type: 'id_proof', name: '', expiry_date: '' }); }, onError: (e) => setErrs(fieldErrors(e)) },
  );
  const verify = useMut((docId: ID) => api.employees.verifyDocument(docId), { ...inv, success: 'Document verified' });
  const remove = useMut((docId: ID) => api.employees.deleteDocument(docId), { ...inv, success: 'Document deleted', onSuccess: () => setDel(null) });
  const choose = async () => {
    try {
      const picked = await pickDocument();
      if (picked) setFile(picked);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not open the file picker');
    }
  };
  const today = todayISO();
  return (
    <Stack gap={12}>
      {manage && (
        <Row>
          <Button variant="primary" icon="upload" title="Upload document" onPress={() => { setErrs({}); setOpen(true); }} testID="doc-upload" />
        </Row>
      )}
      <DataTable
        rows={q.data}
        loading={q.isLoading}
        emptyTitle="No documents"
        columns={[
          { key: 'name', title: 'Name', width: 180, flex: 2 },
          { key: 'type', title: 'Type', width: 120, render: (d) => humanize(d.type), sortValue: (d) => d.type },
          { key: 'expiry_date', title: 'Expiry', width: 110, sortValue: (d) => d.expiry_date, render: (d) => (d.expiry_date ? <Text color={d.expiry_date < today ? colors.danger : undefined}>{fmtDate(d.expiry_date)}</Text> : '—') },
          { key: 'verified_at', title: 'Verified', width: 100, sortValue: (d) => d.verified_at ?? '', render: (d) => (d.verified_at ? <Badge tone="success">Verified</Badge> : <Badge tone="warning">Pending</Badge>) },
          { key: 'created_at', title: 'Uploaded', width: 110, render: (d) => fmtDate(d.created_at), sortValue: (d) => d.created_at },
          {
            key: 'act',
            title: '',
            width: 170,
            flex: 0,
            sortable: false,
            align: 'right',
            render: (d) => (
              <Row gap={2} wrap={false}>
                <IconButton icon="download" size="sm" label={`Download ${d.name}`} onPress={() => void openFile(`/documents/${d.id}/download`)} />
                {manage && !d.verified_at && <Button size="sm" icon="check" title="Verify" loading={verify.isPending && verify.variables === d.id} onPress={() => verify.mutate(d.id)} />}
                {manage && <IconButton icon="trash" size="sm" label={`Delete ${d.name}`} onPress={() => setDel(d.id)} />}
              </Row>
            ),
          },
        ]}
      />
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Upload document"
        width={460}
        footer={
          <>
            <Button title="Cancel" onPress={() => setOpen(false)} />
            <Button variant="primary" title="Upload" loading={upload.isPending} disabled={!file} onPress={() => upload.mutate(undefined)} />
          </>
        }
      >
        <Select label="Type" error={errs.type} value={f.type} onChange={(type) => setF({ ...f, type })} options={DOC_TYPES.map((t) => ({ value: t, label: humanize(t) }))} />
        <TextField label="Name" error={errs.name} placeholder="Defaults to the file name" value={f.name} onChangeText={(name) => setF({ ...f, name })} />
        <DateField label="Expiry date" error={errs.expiry_date} value={f.expiry_date} clearable onChange={(expiry_date) => setF({ ...f, expiry_date })} />
        <View style={{ gap: 6 }}>
          <Text variant="label">
            File <Text color={colors.danger}>*</Text>
          </Text>
          <Row>
            <Button icon="upload" title={file ? 'Choose another' : 'Choose file'} onPress={choose} />
            <Text variant="small" numberOfLines={1} style={{ flexShrink: 1 }}>
              {file ? file.name : DOCUMENT_HINT}
            </Text>
          </Row>
          {!!errs.file && <Text size={12} color={colors.danger}>{errs.file}</Text>}
        </View>
      </Modal>
      <ConfirmDialog open={!!del} onClose={() => setDel(null)} onConfirm={() => del && remove.mutate(del)} title="Delete document?" message="The file will be removed permanently." danger confirmLabel="Delete" loading={remove.isPending} />
    </Stack>
  );
}

function Lifecycle({ id }: { id: ID }) {
  const { colors } = useTheme();
  const q = useQuery({ queryKey: ['employee', id, 'lifecycle'], queryFn: () => api.employees.lifecycle(id) });
  if (q.isLoading) return <Loading />;
  if (!q.data?.length) return <Card><EmptyState icon="list" title="No lifecycle events" /></Card>;
  return (
    <Card>
      <Stack gap={14}>
        {[...q.data]
          .sort((a, b) => b.effective_date.localeCompare(a.effective_date))
          .map((ev) => (
            <Row key={ev.id} align="flex-start" wrap={false} gap={12}>
              <View style={{ width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.primarySoft }}>
                <Icon name={LC_ICON[ev.type] ?? 'info'} size={15} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                <Text>
                  <Text weight="semibold">{humanize(ev.type)}</Text> <Text variant="small">effective {fmtDate(ev.effective_date)}</Text>
                </Text>
                {ev.payload &&
                  Object.entries(ev.payload).map(([k, v]) => (
                    <Text key={k} size={12}>
                      <Text variant="small">{humanize(k)}: </Text>
                      {fmtVal(v)}
                    </Text>
                  ))}
              </View>
            </Row>
          ))}
      </Stack>
    </Card>
  );
}

function Salary({ id }: { id: ID }) {
  const q = useQuery({ queryKey: ['employee', id, 'salary'], queryFn: () => api.employees.salary(id) });
  const current = q.data?.find((s) => !s.effective_to) ?? q.data?.[0];
  return (
    <Stack>
      {current && (
        <Card title={`Current CTC ${inr(current.ctc)}`} subtitle={`${current.salary_structure?.name ?? ''} · from ${fmtDate(current.effective_from)}`}>
          <DataTable
            rows={current.breakdown?.lines ?? []}
            keyExtractor={(l) => l.code}
            columns={[
              { key: 'name', title: 'Component', width: 160, flex: 2 },
              { key: 'type', title: 'Type', width: 120, render: (l) => humanize(l.type), sortValue: (l) => l.type },
              { key: 'monthly', title: 'Monthly', width: 110, align: 'right', render: (l) => inr(l.monthly), sortValue: (l) => l.monthly },
              { key: 'annual', title: 'Annual', width: 120, align: 'right', render: (l) => inr(l.annual), sortValue: (l) => l.annual },
            ]}
          />
        </Card>
      )}
      <Card title="Salary history">
        <DataTable
          rows={q.data}
          loading={q.isLoading}
          emptyTitle="No salary assigned"
          columns={[
            { key: 'effective_from', title: 'From', width: 110, render: (s) => fmtDate(s.effective_from), sortValue: (s) => s.effective_from },
            { key: 'effective_to', title: 'To', width: 110, render: (s) => fmtDate(s.effective_to), sortValue: (s) => s.effective_to },
            { key: 'structure', title: 'Structure', width: 160, render: (s) => s.salary_structure?.name ?? `#${s.salary_structure_id}` },
            { key: 'ctc', title: 'CTC', width: 120, align: 'right', render: (s) => inr(s.ctc), sortValue: (s) => s.ctc },
            { key: 'gross', title: 'Gross / month', width: 120, align: 'right', render: (s) => inr(s.breakdown?.gross_monthly), sortValue: (s) => s.breakdown?.gross_monthly },
          ]}
        />
      </Card>
    </Stack>
  );
}

function AttendanceTab({ id }: { id: ID }) {
  const { colors } = useTheme();
  const [month, setMonth] = useState(currentPeriod());
  const [sel, setSel] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['attendance', 'employee', id, month], queryFn: () => api.attendance.employee(id, month) });
  const byDate = new Map((q.data ?? []).map((d) => [d.date, d]));
  const d = sel ? byDate.get(sel) : undefined;
  const days = q.data ?? [];
  return (
    <Stack>
      <Grid min={140}>
        <Card><Text variant="small">Present</Text><Text variant="value">{days.filter((x) => x.status === 'present' || x.status === 'on_duty').length}</Text></Card>
        <Card><Text variant="small">Absent</Text><Text variant="value">{days.filter((x) => x.status === 'absent').length}</Text></Card>
        <Card><Text variant="small">Late marks</Text><Text variant="value">{days.filter((x) => x.is_late).length}</Text></Card>
        <Card><Text variant="small">LOP days</Text><Text variant="value">{days.reduce((a, x) => a + (x.lop_days || 0), 0)}</Text></Card>
      </Grid>
      <Card>
        {q.isLoading ? (
          <Loading />
        ) : (
          <CalendarMonth
            month={month}
            onMonthChange={setMonth}
            onDayPress={setSel}
            dayBackground={(date) => {
              const s = byDate.get(date)?.status;
              return s ? attBg(colors, s) : undefined;
            }}
            renderDay={(date) => {
              const a = byDate.get(date);
              if (!a) return null;
              return (
                <>
                  <CalChip color={attColor(colors, a.status)}>{humanize(a.status)}</CalChip>
                  {a.worked_minutes > 0 && <Text size={10} variant="small">{minutesToHm(a.worked_minutes)}{a.is_late ? ' · late' : ''}</Text>}
                </>
              );
            }}
          />
        )}
        <View style={{ marginTop: 12 }}>
          <AttendanceLegend colors={colors} />
        </View>
      </Card>
      <Modal open={!!sel} onClose={() => setSel(null)} title={sel ? fmtDate(sel) : ''} width={420}>
        {d ? (
          <>
            <Row>
              <StatusBadge status={d.status} />
              {d.is_late && <Badge tone="warning">{`Late ${d.late_minutes}m`}</Badge>}
              {d.is_locked && <Badge tone="neutral">Locked</Badge>}
            </Row>
            <DescList
              items={[
                ['First in', fmtTime(d.first_in)], ['Last out', fmtTime(d.last_out)], ['Worked', minutesToHm(d.worked_minutes)], ['Overtime', minutesToHm(d.ot_minutes)],
                ['Early exit', `${d.early_exit_minutes}m`], ['LOP days', d.lop_days], ['Remarks', d.remarks],
              ]}
            />
          </>
        ) : (
          <Text variant="muted">No attendance record for this day.</Text>
        )}
      </Modal>
    </Stack>
  );
}

function LeaveTab({ id }: { id: ID }) {
  const b = useQuery({ queryKey: ['leave', 'balances', id], queryFn: () => api.leave.balances(id) });
  const r = useQuery({ queryKey: ['leave', 'requests', 'employee', id], queryFn: () => api.leave.requests({ scope: 'all', employee_id: id }) });
  return (
    <Stack>
      {b.isLoading ? <Loading /> : b.data?.length ? <BalanceCards balances={b.data} /> : <Card><EmptyState icon="leaf" title="No leave balances" /></Card>}
      <DataTable
        rows={r.data}
        loading={r.isLoading}
        emptyTitle="No leave requests"
        columns={[
          { key: 'type', title: 'Type', width: 150, sortValue: (x) => x.leave_type?.name, render: (x) => <Row gap={6}><Dot color={x.leave_type?.color ?? '#888'} /><Text>{x.leave_type?.name ?? '—'}</Text></Row> },
          { key: 'from_date', title: 'From', width: 110, render: (x) => fmtDate(x.from_date), sortValue: (x) => x.from_date },
          { key: 'to_date', title: 'To', width: 110, render: (x) => fmtDate(x.to_date), sortValue: (x) => x.to_date },
          { key: 'days', title: 'Days', width: 70, align: 'right' },
          { key: 'reason', title: 'Reason', width: 160, flex: 2 },
          { key: 'status', title: 'Status', width: 100, render: (x) => <StatusBadge status={x.status} />, sortValue: (x) => x.status },
        ]}
      />
    </Stack>
  );
}

function LifecycleModal({ emp, type, onClose }: { emp: Employee; type: LcType | null; onClose: () => void }) {
  const org = useOrgOptions(!!type);
  const [date, setDate] = useState(todayISO());
  const [payload, setPayload] = useState<Record<string, unknown>>({});
  const [errs, setErrs] = useState<Record<string, string>>({});
  const act = useMut(() => api.employees.lifecycleAction(emp.id, { type: type!, effective_date: date, payload }), {
    invalidate: [['employee'], ['employees'], ['dashboard']],
    success: `${humanize(type ?? '')} recorded`,
    onSuccess: () => {
      setPayload({});
      onClose();
    },
    onError: (e) => setErrs(fieldErrors(e)),
  });
  const sel = (k: string, label: string, options: { value: number; label: string }[]) => (
    <Select<number | ''> key={k} label={label} error={errs[`payload.${k}`]} placeholder="No change" value={(payload[k] as number | undefined) ?? ''} options={[{ value: '', label: 'No change' }, ...options]} onChange={(v) => setPayload({ ...payload, [k]: v === '' ? undefined : v })} />
  );
  const setP = (k: string) => (v: unknown) => setPayload({ ...payload, [k]: v });
  return (
    <Modal
      open={!!type}
      onClose={onClose}
      title={`${humanize(type ?? '')} — ${emp.full_name}`}
      width={560}
      footer={
        <>
          <Button title="Cancel" onPress={onClose} />
          <Button variant={type === 'exit' ? 'danger' : 'primary'} title={`Confirm ${type ?? ''}`} loading={act.isPending} onPress={() => act.mutate(undefined)} testID="lifecycle-confirm" />
        </>
      }
    >
      <DateField label={type === 'exit' ? 'Last working day' : 'Effective date'} required error={errs.effective_date} value={date} onChange={setDate} />
      {type === 'transfer' && (
        <>
          <Grid min={200} gap={14}>
            {sel('location_id', 'New location', org.locations)}
            {sel('department_id', 'New department', org.departments)}
            {sel('legal_entity_id', 'New legal entity', org.legalEntities)}
            {sel('cost_centre_id', 'New cost centre', org.costCentres)}
          </Grid>
          <EmployeePicker label="New manager" value={payload.manager_id as number | null} onChange={(v) => setP('manager_id')(v ?? undefined)} />
        </>
      )}
      {type === 'promote' && (
        <Grid min={200} gap={14}>
          {sel('designation_id', 'New designation', org.designations)}
          {sel('grade_id', 'New grade', org.grades)}
        </Grid>
      )}
      {type === 'exit' && (
        <>
          <Grid min={200} gap={14}>
            <DateField label="Resignation date" value={(payload.resignation_date as string) ?? ''} clearable onChange={(d) => setP('resignation_date')(d || undefined)} />
            <Select
              label="Exit type"
              value={(payload.exit_type as string) ?? 'resignation'}
              onChange={setP('exit_type')}
              options={['resignation', 'termination', 'retirement', 'absconding'].map((v) => ({ value: v, label: humanize(v) }))}
            />
          </Grid>
          <TextField label="Reason" multiline value={(payload.reason as string) ?? ''} onChangeText={setP('reason')} />
          <Alert tone="warning">The employee moves to notice / exited status and full & final settlement becomes available in payroll.</Alert>
        </>
      )}
      {type === 'confirm' && <Text variant="muted">Marks the employee as confirmed and ends probation.</Text>}
      {(type === 'transfer' || type === 'promote') && <TextField label="Remarks" multiline value={(payload.remarks as string) ?? ''} onChangeText={setP('remarks')} />}
    </Modal>
  );
}

/** Full employee record for HR (hidden screen hr/employees/:id). */
export function EmployeeDetailScreen() {
  const id = Number(useParams<{ id: string }>().id);
  const { can } = useAuth();
  const go = useGo();
  const toast = useToast();
  const { colors } = useTheme();
  const [tab, setTab] = useState<TabKey>('overview');
  const [lc, setLc] = useState<LcType | null>(null);
  const [menu, setMenu] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['employee', id], queryFn: () => api.employees.get(id), enabled: !!id });
  const onboard = useMut(() => api.employees.onboardingLink(id), { onSuccess: (r) => setLink(r.url), success: 'Onboarding link generated' });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data)
    return (
      <Screen>
        <PageHeader title="Employee" />
        {q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : <EmptyState icon="user" title="Employee not found" />}
      </Screen>
    );
  const emp = q.data;
  const manage = can(P.EMPLOYEES_MANAGE);
  const tabs: { key: TabKey; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'jobs', label: 'Job history' },
    { key: 'documents', label: 'Documents' },
    { key: 'lifecycle', label: 'Lifecycle' },
    ...(can(P.PAYROLL_VIEW, P.PAYROLL_MANAGE) ? [{ key: 'salary' as const, label: 'Salary' }] : []),
    ...(can(P.ATTENDANCE_VIEW, P.ATTENDANCE_MANAGE, P.ATTENDANCE_TEAM) ? [{ key: 'attendance' as const, label: 'Attendance' }] : []),
    ...(can(P.LEAVE_VIEW, P.LEAVE_MANAGE, P.LEAVE_TEAM) ? [{ key: 'leave' as const, label: 'Leave' }] : []),
  ];
  const actions: { type: LcType; label: string; hint: string }[] = [
    { type: 'confirm', label: 'Confirm', hint: 'End probation' },
    { type: 'transfer', label: 'Transfer', hint: 'Location, department, entity, manager' },
    { type: 'promote', label: 'Promote', hint: 'Designation and grade' },
    { type: 'exit', label: 'Exit', hint: 'Resignation, termination, retirement' },
  ];
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="employee-detail">
      <PageHeader title={emp.full_name} subtitle={[emp.emp_code, emp.designation, emp.department].filter(Boolean).join(' · ')} />
      <Row gap={10}>
        <Avatar name={emp.full_name} size={44} />
        <StatusBadge status={emp.status} />
        {emp.current_job?.location?.name ? <Badge tone="info">{emp.current_job.location.name}</Badge> : null}
        <View style={{ flex: 1 }} />
        {manage && (
          <Row gap={8}>
            {(emp.status === 'onboarding' || emp.status === 'probation') && <Button icon="send" title="Onboarding link" loading={onboard.isPending} onPress={() => onboard.mutate(undefined)} testID="emp-onboarding-link" />}
            <Button icon="more" title="Actions" onPress={() => setMenu(true)} testID="emp-actions" />
            <Button variant="primary" icon="edit" title="Edit" onPress={() => go('EmployeeEdit', { id: emp.id })} testID="emp-edit" />
          </Row>
        )}
      </Row>
      <Tabs<TabKey> tabs={tabs} value={tab} onChange={setTab} />
      {tab === 'overview' && <Overview emp={emp} />}
      {tab === 'jobs' && <JobHistory id={id} />}
      {tab === 'documents' && <Documents id={id} />}
      {tab === 'lifecycle' && <Lifecycle id={id} />}
      {tab === 'salary' && <Salary id={id} />}
      {tab === 'attendance' && <AttendanceTab id={id} />}
      {tab === 'leave' && <LeaveTab id={id} />}
      <Modal open={menu} onClose={() => setMenu(false)} title="Employee actions" width={420} noScroll>
        <View>
          {actions.map((a) => (
            <ListItem key={a.type} icon={LC_ICON[a.type]} title={a.label} subtitle={a.hint} chevron onPress={() => { setMenu(false); setLc(a.type); }} testID={`emp-action-${a.type}`} />
          ))}
          <ListItem icon="file" title="Generate letter" subtitle="Offer, confirmation, relieving…" chevron divider={false} onPress={() => { setMenu(false); go('Letters', { employee: emp.id }); }} />
        </View>
      </Modal>
      <LifecycleModal emp={emp} type={lc} onClose={() => setLc(null)} />
      <Modal
        open={!!link}
        onClose={() => setLink(null)}
        title="Self-onboarding link"
        width={520}
        footer={
          <Button
            variant="primary"
            icon="send"
            title="Copy / share link"
            onPress={() =>
              void copyText(link ?? '').then((r) => {
                if (r === 'copied') toast.success('Link copied');
                else if (r === 'failed') toast.error('Copy failed — select the link and copy it manually');
              })
            }
          />
        }
      >
        <Text variant="muted">Share this link with {emp.first_name} to fill personal, bank and statutory details and upload documents.</Text>
        <View style={{ padding: 10, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.surface2 }}>
          <Text selectable variant="mono">{link}</Text>
        </View>
      </Modal>
    </Screen>
  );
}
